import type { ChecklistType } from '@arhs-fleetback/types/entities/checklists';

import {
  CHECKLISTS_API_URL,
  CHECKLISTS_FILTERS_URL,
  CHECKLISTS_SEARCH_ALTERABLE_URL,
  CHECKLISTS_SEARCH_AVAILABLE_API_URL,
  CHECKLISTS_SEARCH_AVAILABLE_URL,
  CHECKLISTS_SEARCH_BY_DEFAULT_URL,
  DEALERS_API_URL,
} from 'constants/ApiConstants';
import { CHECKLIST_UPSELLING } from 'constants/ChecklistConstants';
import { PaginationType } from 'constants/Global';
import { MODE_ENTITY } from 'constants/InterventionConstants';
import { TECHNICIAN } from 'constants/ModeConstants';
import { MODULES } from 'constants/ModuleConstants';
import * as actionTypes from 'modules/checklists/actionTypes';
import type * as ActionTypes from 'modules/checklists/types';
import type { DetailedChecklist } from 'modules/checklists/types/projections/detailed-checklist';
import type { ApiError, MetaAction } from 'modules/global/generic.types';
import type { ModuleEnum } from 'modules/global/typing/enums/module.enum';

export const searchChecklists = (
  params: Record<string, unknown>,
  meta: MetaAction & { alterable?: boolean } = {}
): ActionTypes.SearchChecklistsAction => {
  const { alterable } = meta;
  const url = alterable ? CHECKLISTS_SEARCH_ALTERABLE_URL : CHECKLISTS_SEARCH_AVAILABLE_API_URL;

  return {
    type: actionTypes.SEARCH_CHECKLISTS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url,
        params,
      },
    },
    meta,
  };
};

export const searchChecklistsSuccess = (
  data: ActionTypes.SearchChecklistsSuccessActionPayload,
  meta: MetaAction = {}
): ActionTypes.SearchChecklistsSuccessAction => ({
  type: actionTypes.SEARCH_CHECKLISTS_SUCCESS,
  payload: data,
  meta,
});

export const searchChecklistsFailure = (message: ApiError, meta: MetaAction = {}): ActionTypes.SearchChecklistsFailureAction => ({
  type: actionTypes.SEARCH_CHECKLISTS_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const searchExtras = (params: Record<string, unknown> = {}, meta: MetaAction = {}): ActionTypes.SearchChecklistsAction =>
  searchChecklists(
    {
      page: 0,
      size: 10,
      projection: 'details',
      types: CHECKLIST_UPSELLING,
      ...params,
    },
    { alterable: true, ...meta }
  );

export const fetchChecklist = (checklistId: string, meta: MetaAction = {}): ActionTypes.FetchChecklistAction => ({
  type: actionTypes.FETCH_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${CHECKLISTS_API_URL}/${checklistId}`,
      params: {
        id: checklistId,
        projection: 'details',
      },
    },
  },
  meta,
});

export const fetchChecklists = (params: { ids: string[] }, meta: MetaAction = {}): ActionTypes.FetchChecklistAction => ({
  type: actionTypes.MULTIPLE_FETCH_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: CHECKLISTS_FILTERS_URL,
      params: {
        ...params,
        projection: 'details',
      },
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

export const fetchChecklistSuccess = (
  checklist: DetailedChecklist,
  meta: MetaAction = {}
): ActionTypes.FetchChecklistSuccessAction => ({
  type: actionTypes.FETCH_SUCCESS,
  payload: checklist,
  meta,
});

export const fetchChecklistFailure = (message: ApiError, meta: MetaAction = {}): ActionTypes.FetchChecklistFailureAction => ({
  type: actionTypes.FETCH_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const deleteChecklist = (checklistId: string, type: ChecklistType): ActionTypes.DeleteChecklistAction => ({
  type: actionTypes.DELETE_CHECKLIST_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${CHECKLISTS_API_URL}/${checklistId}`,
    },
    type,
    checklistId,
  },
});

export const deleteChecklistSuccess = (
  checklistId: string,
  meta: MetaAction = {}
): ActionTypes.DeleteChecklistSuccessAction => ({
  type: actionTypes.DELETE_CHECKLIST_SUCCESS,
  payload: checklistId,
  meta,
});

export const deleteChecklistFailure = (error: ApiError, meta: MetaAction = {}): ActionTypes.DeleteChecklistFailureAction => ({
  type: actionTypes.DELETE_CHECKLIST_FAILURE,
  payload: error,
  error: true,
  meta,
});

type FetchChecklistsSelectParams = {
  ids?: string[];
  dealerId?: string;
  dealerIds?: string[];
};

const getFetchChecklistsSelectAdditionalParams = (params: FetchChecklistsSelectParams) => {
  if (Array.isArray(params.ids)) {
    return {
      fields: undefined,
      size: params.ids.length,
    };
  }

  const isScopedToDealer = params.dealerId || (params.dealerIds?.length ?? 0) > 0;
  return {
    fields: 'id,name,dealerId,groupId,type',
    sort: isScopedToDealer ? ['name,asc', 'dealerId,asc', 'groupId,asc'] : ['name,asc', 'groupId,asc'],
  };
};

export const fetchChecklistsSelect = (
  params: FetchChecklistsSelectParams,
  meta = {}
): ActionTypes.FetchChecklistsSelectAction => {
  const additionalParams = getFetchChecklistsSelectAdditionalParams(params);

  return {
    type: actionTypes.FETCH_CHECKLISTS_SELECT_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: CHECKLISTS_SEARCH_AVAILABLE_URL,
        params: {
          page: 0,
          size: 20,
          paginationType: PaginationType.SLICE,
          ...additionalParams,
          ...params,
        },
      },
    },
    meta,
  };
};

export const fetchChecklistsSelectSuccess = (
  data: ActionTypes.SearchChecklistsSuccessActionPayload,
  meta: MetaAction = {}
): ActionTypes.FetchChecklistsSelectSuccessAction => ({
  type: actionTypes.FETCH_CHECKLISTS_SELECT_SUCCESS,
  payload: data,
  meta,
});

export const fetchChecklistsSelectFailure = (
  message: ApiError,
  meta: MetaAction = {}
): ActionTypes.FetchChecklistsSelectFailureAction => ({
  type: actionTypes.FETCH_CHECKLISTS_SELECT_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const cleanChecklistsSelect = (
  payload: undefined,
  meta: MetaAction = {}
): ActionTypes.CleanChecklistsSelectAction => ({
  type: actionTypes.CLEAN_CHECKLISTS_SELECT,
  payload,
  meta,
});

export const cleanChecklists = (payload: undefined, meta: MetaAction = {}): ActionTypes.CleanChecklistsAction => ({
  type: actionTypes.CLEAN_CHECKLISTS,
  payload,
  meta,
});

export const displayDuplicateModal = (
  checklistId: string,
  meta: MetaAction = {}
): ActionTypes.DisplayDuplicateModalAction => ({
  type: actionTypes.DISPLAY_CHECKLIST_DUPLICATE_MODAL,
  payload: checklistId,
  meta,
});

export const hideDuplicateModal = (meta: MetaAction = {}): ActionTypes.HideDuplicateModalAction => ({
  type: actionTypes.HIDE_CHECKLIST_DUPLICATE_MODAL,
  payload: undefined,
  meta,
});

export const fetchDefaultChecklist = (
  params: Record<string, unknown> = {},
  meta: MetaAction = {}
): ActionTypes.FetchDefaultChecklistAction => ({
  type: actionTypes.FETCH_DEFAULT_CHECKLIST_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: CHECKLISTS_SEARCH_BY_DEFAULT_URL,
      params,
    },
  },
  meta,
});

export const fetchDefaultChecklistSuccess = (
  checklist: DetailedChecklist,
  meta: MetaAction = {}
): ActionTypes.FetchDefaultChecklistSuccessAction => ({
  type: actionTypes.FETCH_DEFAULT_CHECKLIST_SUCCESS,
  payload: checklist,
  meta,
});

export const fetchDefaultChecklistFailure = (
  message: ApiError,
  meta: MetaAction = {}
): ActionTypes.FetchDefaultChecklistFailureAction => ({
  type: actionTypes.FETCH_DEFAULT_CHECKLIST_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const createFromDefaultChecklist = (
  parameters: ActionTypes.CreateFromDefaultChecklistActionPayload,
  meta: MetaAction = {}
): ActionTypes.CreateFromDefaultChecklistAction => ({
  type: actionTypes.CREATE_CHECKLIST_FROM_DEFAULT_CHECKLIST,
  payload: parameters,
  meta,
});

export const duplicateChecklist = (
  { id, ...data }: { id: string } & ActionTypes.DuplicateChecklistData,
  meta: MetaAction = {}
): ActionTypes.DuplicateChecklistRequest => ({
  type: actionTypes.DUPLICATE_CHECKLIST_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${CHECKLISTS_API_URL}/${id}/clone`,
      data,
    },
  },
  meta,
});

export const duplicateChecklistSuccess = (
  payload: DetailedChecklist,
  meta: MetaAction = {}
): ActionTypes.DuplicateChecklistSuccess => ({
  type: actionTypes.DUPLICATE_CHECKLIST_SUCCESS,
  payload,
  meta,
});

export const duplicateChecklistFailure = (error: ApiError, meta: MetaAction = {}): ActionTypes.DuplicateChecklistFailure => ({
  type: actionTypes.DUPLICATE_CHECKLIST_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchSelectedChecklists = (
  { dealerId, brandId, module }: { dealerId: string; brandId: string; module: ModuleEnum },
  meta: MetaAction = {}
): ActionTypes.FetchSelectedChecklistsRequest => {
  const mode = MODULES[module]?.mode;
  const entity = MODE_ENTITY[mode as keyof typeof MODE_ENTITY]?.toLowerCase() ?? MODE_ENTITY[TECHNICIAN];

  return {
    type: actionTypes.FETCH_SELECTED_CHECKLISTS_REQUEST,
    payload: {
      request: {
        method: 'GET',
        url: `${DEALERS_API_URL}/${dealerId}/config/${entity}/selectedChecklists`,
        params: {
          brandId,
          module,
        },
      },
      dealerId,
    },
    meta,
  };
};

export const fetchSelectedChecklistsSuccess = (
  payload: { dealerId: string; content: DetailedChecklist[] },
  meta: MetaAction = {}
): ActionTypes.FetchSelectedChecklistsSuccess => ({
  type: actionTypes.FETCH_SELECTED_CHECKLISTS_SUCCESS,
  payload,
  meta,
});

export const fetchSelectedChecklistsFailure = (
  error: ApiError,
  meta: MetaAction = {}
): ActionTypes.FetchSelectedChecklistsFailure => ({
  type: actionTypes.FETCH_SELECTED_CHECKLISTS_FAILURE,
  payload: error,
  error: true,
  meta,
});
